import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, Settings as SettingsIcon, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TableView = () => {
    const [tables, setTables] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'support';
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState("");

    // Load branches
    useEffect(() => {
        if (isAdmin) {
            const fetchBranches = async () => {
                try {
                    const res = await axios.get(`${API_URL}/admin/branches`, { withCredentials: true });
                    const activeBranches = res.data.filter(b => b.active);
                    setBranches(activeBranches);
                    if (activeBranches.length > 0) setSelectedBranch(activeBranches[0].branch_id);
                } catch (e) { console.error(e); }
            };
            fetchBranches();
        }
    }, [isAdmin]);

    // Colores por estado
    const STATUS_COLORS = {
        available: { bg: '#10B981', border: '#059669', text: 'Libre' },
        occupied: { bg: '#EF4444', border: '#DC2626', text: 'Ocupada' },
        waiting_payment: { bg: '#F59E0B', border: '#D97706', text: 'Esperando pago' },
        reserved: { bg: '#3B82F6', border: '#2563EB', text: 'Reservada' }
    };

    useEffect(() => {
        if (selectedBranch || !isAdmin) {
            loadTables();
            const interval = setInterval(loadTables, 10000);
            return () => clearInterval(interval);
        }
    }, [selectedBranch]);

    const loadTables = async () => {
        try {
            const config = {};
            if (selectedBranch) {
                config.headers = { 'X-Branch-Id': selectedBranch };
            }
            const response = await axios.get(`${API_URL}/tables`, {
                ...config,
                withCredentials: true
            });
            setTables(response.data);
        } catch (error) {
            console.error('Error cargando mesas:', error);
        }
    };

    const handleTableClick = async (table) => {
        if (table.status === 'available') {
            // Crear nuevo pedido para esta mesa
            navigate(`/admin/pos?table=${table.number}`);
        } else if (table.current_order_id && !table.current_order_id.includes('mock')) {
            // Ir al POS para EDITAR (Agregar productos o cobrar)
            navigate(`/admin/pos?edit_order=${table.current_order_id}`);
        } else if (table.status === 'waiting_payment') {
            // Ir al POS para cobrar
            navigate(`/admin/pos?view=pending&search=${table.number}`);
        } else {
            // Por defecto si está ocupada, ir al POS con esa mesa por si quieren añadir más cosas o cobrar
            navigate(`/admin/pos?view=pending&search=${table.number}`);
        }
    };

    const getFilteredTables = () => {
        if (selectedFilter === 'all') return tables;
        return tables.filter(t => t.status === selectedFilter);
    };

    const getStatusStats = () => {
        return {
            available: tables.filter(t => t.status === 'available').length,
            occupied: tables.filter(t => t.status === 'occupied').length,
            waiting_payment: tables.filter(t => t.status === 'waiting_payment').length,
            reserved: tables.filter(t => t.status === 'reserved').length
        };
    };

    const stats = getStatusStats();
    const filteredTables = getFilteredTables();

    // Renderizar una mesa en lista
    const renderTableCard = (table) => {
        const statusColor = STATUS_COLORS[table.status] || STATUS_COLORS.available;

        let borderColorClass = '';
        let bgColorClass = '';
        let textColorClass = '';
        let animationClass = '';

        switch (table.status) {
            case 'occupied':
                borderColorClass = 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.2)]';
                bgColorClass = 'bg-red-500/10';
                textColorClass = 'text-red-400';
                break;
            case 'waiting_payment':
                borderColorClass = 'border-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.2)]';
                bgColorClass = 'bg-yellow-500/10';
                textColorClass = 'text-yellow-400';
                animationClass = 'animate-pulse';
                break;
            case 'reserved':
                borderColorClass = 'border-blue-500';
                bgColorClass = 'bg-blue-500/10';
                textColorClass = 'text-blue-400';
                break;
            case 'available':
            default:
                borderColorClass = 'border-green-500/50 hover:border-green-400';
                bgColorClass = 'bg-gray-800 hover:bg-gray-750';
                textColorClass = 'text-green-400';
                break;
        }

        return (
            <div
                key={table.table_id}
                onClick={() => handleTableClick(table)}
                className={`relative p-5 rounded-xl border-2 transition-all cursor-pointer transform hover:-translate-y-1 hover:shadow-xl ${borderColorClass} ${bgColorClass} bg-gray-900`}
            >
                {/* Badge de estado */}
                <div
                    className={`absolute -top-3 right-3 px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm ${animationClass}`}
                    style={{ backgroundColor: statusColor.bg }}
                >
                    {statusColor.text}
                </div>

                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-2xl font-bold text-white">
                        Mesa {table.number}
                    </h3>
                    <span className="text-sm text-gray-400 font-medium bg-gray-800 px-2 py-1 rounded">
                        👥 {table.seats}
                    </span>
                </div>

                {table.zone && (
                    <div className="text-xs text-gray-500 mb-2 uppercase tracking-wide font-semibold">
                        📍 {table.zone}
                    </div>
                )}

                {table.current_order_id && (
                    <div className="mt-3 bg-gray-950 border border-gray-800 rounded p-2 flex items-center gap-2">
                        <span className="text-lg">🧾</span>
                        <div>
                            <p className="text-[10px] text-gray-500 uppercase">Pedido Activo</p>
                            <p className="text-xs font-mono text-blue-400 truncate w-32">
                                {table.current_order_id.replace('ORD-', '')}
                            </p>
                        </div>
                    </div>
                )}

                <div className="mt-4 pt-3 border-t border-gray-800/50">
                    <div className={`text-xs font-bold flex items-center gap-1 ${textColorClass}`}>
                        {table.status === 'available' ? (
                            <>👆 CREAR PEDIDO</>
                        ) : (
                            <>👁️ VER / EDITAR</>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-950 p-6 font-sans text-white">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(isAdmin ? '/admin' : '/admin/pos')}
                        className="rounded-full hover:bg-gray-800 text-gray-400 hover:text-white"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold text-white tracking-tight">Mapa de Mesas</h1>
                        <p className="text-gray-500 text-sm">Gestión de sala en tiempo real</p>
                    </div>
                </div>

                {isAdmin && (
                    <button
                        onClick={() => navigate('/admin/tables/editor')}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-lg border border-gray-700 transition-all hover:border-gray-500"
                    >
                        <SettingsIcon className="w-4 h-4" />
                        Editar Distribución
                    </button>
                )}
            </div>

            {/* Branch Selector */}
            {isAdmin && (
                <div className="flex justify-end mb-6">
                    <div className="flex items-center gap-2">
                        <label className="text-gray-400 text-sm">Ver sucursal:</label>
                        <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                            <SelectTrigger className="w-[200px] bg-gray-900 border-gray-700 text-white">
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <SelectValue placeholder="Seleccionar..." />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                {branches.map(b => (
                                    <SelectItem key={b.branch_id} value={b.branch_id}>
                                        {b.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div
                    onClick={() => setSelectedFilter('all')}
                    className={`p-5 rounded-xl border-2 transition-all cursor-pointer bg-gray-900 ${selectedFilter === 'all' ? 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.2)]' : 'border-gray-800 hover:border-gray-700'
                        }`}
                >
                    <div className="text-3xl font-bold text-white mb-1">{tables.length}</div>
                    <div className="text-sm text-gray-400 font-medium">Mesas Totales</div>
                </div>

                <div
                    onClick={() => setSelectedFilter('available')}
                    className={`p-5 rounded-xl border-2 transition-all cursor-pointer bg-gray-900 ${selectedFilter === 'available' ? 'border-green-500 shadow-[0_0_15px_rgba(16,185,129,0.2)]' : 'border-gray-800 hover:border-gray-700'
                        }`}
                >
                    <div className="text-3xl font-bold text-green-500 mb-1">{stats.available}</div>
                    <div className="text-sm text-gray-400 font-medium">Libres</div>
                </div>

                <div
                    onClick={() => setSelectedFilter('occupied')}
                    className={`p-5 rounded-xl border-2 transition-all cursor-pointer bg-gray-900 ${selectedFilter === 'occupied' ? 'border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.2)]' : 'border-gray-800 hover:border-gray-700'
                        }`}
                >
                    <div className="text-3xl font-bold text-red-500 mb-1">{stats.occupied}</div>
                    <div className="text-sm text-gray-400 font-medium">Ocupadas</div>
                </div>

                <div
                    onClick={() => setSelectedFilter('waiting_payment')}
                    className={`p-5 rounded-xl border-2 transition-all cursor-pointer bg-gray-900 ${selectedFilter === 'waiting_payment' ? 'border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.2)]' : 'border-gray-800 hover:border-gray-700'
                        }`}
                >
                    <div className="text-3xl font-bold text-yellow-500 mb-1 animate-pulse">{stats.waiting_payment}</div>
                    <div className="text-sm text-gray-400 font-medium">Esperando Pago</div>
                </div>
            </div>

            {/* Mesas Grid */}
            {filteredTables.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-in fade-in duration-500">
                    {filteredTables.map(table => renderTableCard(table))}
                </div>
            ) : (
                <div className="bg-gray-900 border border-dashed border-gray-800 rounded-xl p-16 text-center">
                    <div className="text-6xl mb-4 opacity-30">🪑</div>
                    <h3 className="text-xl font-bold text-white mb-2">No hay mesas encontradas</h3>
                    <p className="text-gray-500 text-sm mb-6">
                        No hay mesas que coincidan con el filtro seleccionado o no has creado ninguna.
                    </p>
                    {isAdmin && (
                        <button
                            onClick={() => navigate('/admin/tables/editor')}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-colors"
                        >
                            Crear Distribución
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default TableView;
